import React from 'react';
import { useId } from '@reach/auto-id';

import { useField, useFieldDescription } from '../../context/form.context';
import { ErrorMessage } from '../error-message.component';
import { Listbox, ListboxCommonProps, ListboxMultiple } from '../listbox';

export type ListboxFieldProps = {
  name: string;
  label?: string;
  className?: string;
  helpText?: string;
  multiple?: boolean;
} & Omit<
  React.ComponentProps<typeof Listbox>,
  | 'value'
  | 'hasError'
  | 'errorId'
  | 'required'
  | 'fullWidth'
  | 'onChange'
  | 'onMenuBlur'
>;

export const ListboxField = ({
  name,
  label: labelProp,
  className,
  helpText,
  multiple,
  ...rest
}: ListboxFieldProps) => {
  const [{ value }, meta, helpers] = useField(name);
  const { fieldLabel, required } = useFieldDescription(name);
  const label = labelProp ?? fieldLabel;
  const { error, touched } = meta;
  const hasError = Boolean(error && touched);
  const errorId = useId()!;

  const Component: React.ComponentType<
    ListboxCommonProps<any> & {
      value: any;
      onChange: (value: any) => void;
    }
  > = multiple ? ListboxMultiple : Listbox;

  return (
    <div className={className}>
      <Component
        value={value}
        label={label}
        onChange={(value: any) => helpers.setValue(value)}
        onMenuBlur={() => helpers.setTouched(true)}
        hasError={hasError}
        errorId={hasError ? errorId : undefined}
        required={required}
        {...rest}
      />
      {hasError && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </div>
  );
};
